import { useMemo, useState } from 'react'

interface AccessPoint {
  label: string
  value: number
  date: Date
}

interface AccessChartProps {
  data: AccessPoint[]
  height?: number
}

// Converte uma série de pontos numa curva suave (Catmull-Rom → Bézier),
// dando um aspeto mais "profissional" ao gráfico do que linhas retas.
function smoothPath(points: { x: number; y: number }[]) {
  if (points.length === 0) return ''
  if (points.length === 1) return `M ${points[0].x},${points[0].y}`

  let d = `M ${points[0].x},${points[0].y}`
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i === 0 ? 0 : i - 1]
    const p1 = points[i]
    const p2 = points[i + 1]
    const p3 = points[i + 2 < points.length ? i + 2 : i + 1]
    const cp1x = p1.x + (p2.x - p0.x) / 6
    const cp1y = p1.y + (p2.y - p0.y) / 6
    const cp2x = p2.x - (p3.x - p1.x) / 6
    const cp2y = p2.y - (p3.y - p1.y) / 6
    d += ` C ${cp1x.toFixed(2)},${cp1y.toFixed(2)} ${cp2x.toFixed(2)},${cp2y.toFixed(2)} ${p2.x.toFixed(2)},${p2.y.toFixed(2)}`
  }
  return d
}

export default function AccessChart({ data, height = 208 }: AccessChartProps) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)

  const width = 100
  const chartHeight = 100

  const maxValue = Math.max(...data.map(d => d.value), 1)
  const minValue = Math.min(...data.map(d => d.value), 0)
  const range = Math.max(maxValue - minValue, 1)

  const points = useMemo(() => {
    if (data.length === 0) return []
    const step = data.length > 1 ? width / (data.length - 1) : 0
    return data.map((d, i) => ({
      x: i * step,
      y: chartHeight - ((d.value - minValue) / range) * (chartHeight - 10) - 5,
    }))
  }, [data, minValue, range])

  const linePath = useMemo(() => smoothPath(points), [points])
  const areaPath =
    points.length > 0
      ? `${linePath} L ${points[points.length - 1].x},${chartHeight} L ${points[0].x},${chartHeight} Z`
      : ''

  const peakIndex = useMemo(
    () => data.reduce((best, d, i) => (d.value > data[best].value ? i : best), 0),
    [data]
  )

  const labelStep = data.length <= 7 ? 1 : data.length <= 14 ? 2 : 5

  if (data.length === 0) return null

  return (
    <div>
      <div className="relative" style={{ height }}>
        <svg
          viewBox={`0 0 ${width} ${chartHeight}`}
          preserveAspectRatio="none"
          className="w-full h-full overflow-visible"
        >
          <defs>
            <linearGradient id="accessAreaGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#dc2626" stopOpacity="0.32" />
              <stop offset="100%" stopColor="#dc2626" stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Linhas de grade horizontais */}
          {[0.25, 0.5, 0.75].map(f => (
            <line
              key={f}
              x1={0}
              x2={width}
              y1={chartHeight * f}
              y2={chartHeight * f}
              stroke="currentColor"
              strokeWidth="0.3"
              className="text-gray-200 dark:text-gray-800"
            />
          ))}

          {/* Área preenchida sob a curva */}
          {areaPath && <path d={areaPath} fill="url(#accessAreaGradient)" />}

          {/* Curva principal */}
          {linePath && (
            <path
              d={linePath}
              fill="none"
              stroke="#dc2626"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          )}

          {/* Marcador do pico do período */}
          {points[peakIndex] && (
            <circle
              cx={points[peakIndex].x}
              cy={points[peakIndex].y}
              r={1.8}
              fill="#dc2626"
              stroke="white"
              strokeWidth="0.6"
            />
          )}

          {/* Pontos interativos para tooltip */}
          {points.map((p, i) => (
            <g key={i}>
              <circle
                cx={p.x}
                cy={p.y}
                r={3.2}
                fill="transparent"
                className="cursor-pointer"
                onMouseEnter={() => setHoverIndex(i)}
                onMouseLeave={() => setHoverIndex(null)}
              />
              {hoverIndex === i && (
                <circle cx={p.x} cy={p.y} r={1.9} fill="#dc2626" stroke="white" strokeWidth="0.6" />
              )}
            </g>
          ))}
        </svg>

        {/* Tooltip */}
        {hoverIndex !== null && points[hoverIndex] && (
          <div
            className="absolute -translate-x-1/2 -translate-y-full pointer-events-none z-10"
            style={{
              left: `${points[hoverIndex].x}%`,
              top: `${Math.max(points[hoverIndex].y - 4, 0)}%`,
            }}
          >
            <div className="bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs font-semibold rounded-md px-2 py-1 shadow-lg whitespace-nowrap">
              {data[hoverIndex].value.toLocaleString('pt-BR')} acessos
              <div className="text-[10px] font-normal opacity-70">{data[hoverIndex].label}</div>
            </div>
          </div>
        )}
      </div>

      {/* Eixo X */}
      <div className="flex justify-between mt-2 px-0.5">
        {data.map((d, i) => (
          <span
            key={i}
            className={`text-[9px] sm:text-[10px] text-gray-400 dark:text-gray-500 flex-1 ${
              i % labelStep === 0 || i === data.length - 1 ? '' : 'invisible'
            } ${i === 0 ? 'text-left' : i === data.length - 1 ? 'text-right' : 'text-center'}`}
          >
            {d.label}
          </span>
        ))}
      </div>
    </div>
  )
}
